// The MARKETING chrome: landing, docs, pricing, security, sign-in.
//
// One nav, three links, and a single filled CTA — **Console** — which is the only
// door into the product. That's the whole navigation model for a visitor, and
// it's deliberately smaller than what was here before (Overview / How it works /
// Docs / Pricing / Your apps / Admin / /server, plus a session pill). Seven
// destinations for someone who hasn't signed up yet isn't a menu, it's a maze.
//
// The console has its OWN chrome (ConsoleLayout): a product surface, not a
// marketing page, so it doesn't inherit this nav — rendering it here would nest
// two navigations.

import { Link, useLocation } from "react-router-dom";
import { c, font } from "../design/tokens.ts";
import { CONSOLE_ORIGIN, onConsoleHost } from "../lib/hosts.ts";
import { ConsolePill, Footer, Wordmark } from "./Footer.tsx";

const NAV: Array<{ label: string; to?: string; href?: string; quiet?: boolean }> = [
	{ label: "Docs", to: "/docs" },
	{ label: "Pricing", to: "/pricing" },
	{ label: "llms.txt", href: "/llms.txt", quiet: true },
];

export function Shell({ children }: { children: React.ReactNode }) {
	const { pathname } = useLocation();

	// The console owns the full viewport — and it has its OWN host, where the
	// paths are "/" and "/{slug}". The old check was `pathname.startsWith(
	// "/console")`, which never matches on console.s3p.dev, so the marketing nav
	// rendered ABOVE the console's own bar: two top bars, one page.
	if (onConsoleHost() || pathname.startsWith("/console")) return <>{children}</>;

	// The landing lays out its own full-bleed sections; everything else gets a
	// readable column.
	const fullBleed = pathname === "/";

	return (
		<div
			style={{
				fontFamily: font.sans,
				color: c.ink,
				background: c.bg,
				minHeight: "100vh",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<nav
				style={{
					maxWidth: 1120,
					width: "100%",
					margin: "0 auto",
					boxSizing: "border-box",
					padding: "24px 32px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 24,
				}}
			>
				<Link to="/" style={{ textDecoration: "none" }}>
					<Wordmark />
				</Link>

				<div
					style={{ display: "flex", gap: 26, fontSize: 14, fontWeight: 600, alignItems: "center" }}
				>
					{NAV.map((n) =>
						n.to ? (
							<Link
								key={n.label}
								to={n.to}
								style={{ textDecoration: "none", color: n.quiet ? c.muted : c.ink }}
							>
								{n.label}
							</Link>
						) : (
							<a
								key={n.label}
								href={n.href}
								style={{ textDecoration: "none", color: n.quiet ? c.muted : c.ink }}
							>
								{n.label}
							</a>
						),
					)}
					<ConsolePill href={CONSOLE_ORIGIN} label="Console" />
				</div>
			</nav>

			<main
				style={{
					flex: 1,
					width: "100%",
					...(fullBleed
						? {}
						: {
								maxWidth: 900,
								margin: "0 auto",
								boxSizing: "border-box",
								padding: "8px 32px 72px",
							}),
				}}
			>
				{children}
			</main>

			<Footer />
		</div>
	);
}
